import { useIntl } from "react-intl";
import moment from 'moment';
import { useState, useEffect, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";

//Components
import DataViewInfiniteScroll from 'Components/DataViewInfiniteScroll';
import { Grid, Typography, Container, Paper, Divider } from "@mui/material";
import { NotificationManager } from 'Components/Notifications';
import Filters from '../.components/filters';
import { NavLink } from "react-router-dom";

//Actions
import { dmsStatusRequest, clearRedux } from 'Redux/actions';

//Helpers
import { getDateFormatted } from 'Core/data/Helpers';
import cx from 'classnames';
import { set } from "lodash";


const ROWS_PER_PAGE = 20;
const RANGE_MODE_ROWS = 50;
const FORCE_CURRENT_WEEK_RANGE = false; // true = always use current week dates on mount, ignoring saved preferences

const DMSStatuses = (props) => {
    const { timezone } = props;

    const tableRef = useRef();
    const dispatch = useDispatch();
    const { messages } = useIntl();

    const { Items, loading, hasMore, errorCode, error } = useSelector(state => state.dmsStatusesRedux)
    const [columns, setColumns] = useState([
        {
            key: "camerasn",
            title: messages['vidfleet_camera_sm'],
            show: true,
            locked: true,
            resize: true,
            export: true,
            exportCell: "cameraSN",
            code: "vidfleet_camera_sm",
            render: (item) => {
                return (
                    <NavLink to={`/app/devices/overview/${item?.cameraSN}`} style={{ color: 'black' }}>
                        <span style={{ textDecoration: 'underline', cursor: 'pointer' }}>{item?.cameraSN}</span>
                    </NavLink>
                )
            }
        },
        {
            key: "previousstatus",
            title: messages['vidfleet_previous_status'],
            show: true,
            locked: false,
            resize: true,
            export: true,
            exportCell: "previousStatus",
            code: "vidfleet_previous_status",
            render: (item) => {
                return <span>{item.previousStatus}</span>
            }
        },
        {
            key: "currentstatus",
            title: messages['vidfleet_current_status'],
            show: true,
            locked: false,
            resize: true,
            export: true,
            exportCell: "currentStatus",
            code: "vidfleet_current_status",
            render: (item) => {
                return <span>{item.currentStatus}</span>
            }
        },
        {
            key: "changetime",
            title: messages['vidfleet_change_time'],
            show: true,
            locked: false,
            resize: true,
            export: true,
            exportCell: "changeTime",
            exportFormat: "datetime",
            code: "vidfleet_change_time",
            render: (item) => {
                return <span>{item?.changeTime && getDateFormatted(item?.changeTime, "date", "datetime", null, timezone)}</span>
            }
        },
    ])

    const rawDataFilters = useSelector(state => state.rawDataTabRedux.filters);

    const current_week = moment().startOf('week');
    const tempStartDate = new Date(current_week.toString());
    const [startDate, setStartDate] = useState(FORCE_CURRENT_WEEK_RANGE ? tempStartDate : (rawDataFilters?.startDate || tempStartDate));
    const [endDate, setEndDate] = useState(FORCE_CURRENT_WEEK_RANGE ? new Date() : (rawDataFilters?.endDate || new Date()));
    const [beforeRangeDate, setBeforeRangeDate] = useState({
        start: tempStartDate,
        end: endDate
    })
    const [last, setLast] = useState(rawDataFilters?.last || { id: '20', name: '20' });
    const [type, setType] = useState(rawDataFilters?.type || 'last');
    const [esn, setEsn] = useState(props?.single ? props?.device?.esn || "" : "");

    const getData = (refresh, paramType) => {
        const currentType = paramType ?? type;
        if (refresh) dispatch(clearRedux("DMS_STATUS", { hasMore: false, Items: [] }));

        let currentStarDate = new Date(startDate);
        let currentEndDate = new Date(endDate);

        let payload = {
            startDate: currentStarDate.setHours(0),
            endDate: currentEndDate.setHours(23, 59, 59),
            from: refresh ? 0 : Items.length || 0,
            limit: currentType === "last" ? last.id : ROWS_PER_PAGE,
            moreRecords: false,
            hasMore: refresh ? false : currentType === "last" ? false : hasMore,
            filterByDate: currentType === "range",
            esn: esn
        }
        if (payload.startDate && payload.endDate) {
            payload.startDate = new Date(payload.startDate)
            payload.endDate = new Date(payload.endDate)
        }

        dispatch(dmsStatusRequest(payload));
        tableRef?.current?.conditionsHandler(payload)
    }

    const handleLast = (type) => {
        dispatch(clearRedux("DMS_STATUS", { hasMore: false }))
        setType(type);
        if (type === 'range') {
            tableRef?.current?.setRows(RANGE_MODE_ROWS);
        }
        getData(true, type);
    }

    const handleChangeConditions = (tags, newConditions, total, newColumns, rows, sort, key, fromPreferences) => {
        let currentStartDate = new Date(startDate);
        let currentEndDate = new Date(endDate);
        let currentType = type;

        // Sync local state from preferences when loaded from saved preferences
        if (fromPreferences && newConditions) {
            if (newConditions.filterByDate && newConditions.startDate) {
                currentType = 'range';
                setType('range');
                if (!FORCE_CURRENT_WEEK_RANGE) {
                    currentStartDate = new Date(newConditions.startDate);
                    currentEndDate = new Date(newConditions.endDate);
                    setStartDate(currentStartDate);
                    setEndDate(currentEndDate);
                    setBeforeRangeDate({
                        start: currentStartDate,
                        end: currentEndDate
                    });
                }
            } else if (newConditions.limit) {
                currentType = 'last';
                setType('last');
                setLast({ id: String(newConditions.limit), name: String(newConditions.limit) });
            }
        }

        let payload = {
            startDate: new Date(currentStartDate).setHours(0),
            endDate: new Date(currentEndDate).setHours(23, 59, 59),
            from: 0,
            limit: currentType === "last" ? newConditions?.limit || last.id : ROWS_PER_PAGE,
            moreRecords: false,
            hasMore: false,
            type: currentType,
            filterByDate: currentType === "range",
            esn: esn,
        };

        if (payload.startDate && payload.endDate) {
            payload.startDate = new Date(payload.startDate);
            payload.endDate = new Date(payload.endDate);
        }

        dispatch(dmsStatusRequest(payload));
    };

    useEffect(() => {

        return () => {
            dispatch(clearRedux("DMS_STATUS"));
        };
    }, [])

    useEffect(() => {
        if (error) {
            if (esn) {
                NotificationManager.error(
                    errorCode === 403 ? messages['vidfleet_the_esn_could_not_be_consulted'] : messages['vidfleet_the_esn_does_not_exist'],
                    messages['vidfleet_dms_statuses'],
                    3000,
                    null,
                    null,
                    'error'
                );
            } else {
                NotificationManager.error(
                    messages['vidfleet_error_dms_statuses'],
                    messages['vidfleet_dms_statuses'],
                    3000,
                    null,
                    null,
                    'error'
                );
            }
            dispatch(clearRedux("DMS_STATUS", { error: null, errorCode: null }));
        }
    }, [error])

    return (
        <Container maxWidth={false} className={cx("vidfleet-dms-statuses-tab", !props?.single ? "container-view" : "container-view-tab")}>
            <Paper elevation={0}>
                <Grid
                    container
                    direction="row"
                    justifyContent="flex-start"
                    alignItems="center"
                    style={{ padding: !props?.single ? "14px 18px" : 0, borderBottom: "1px solid #e8e8e8", height: '95v' }}
                >
                    {
                        !props?.single &&
                        <Grid item xs={6}>
                            <Typography variant="h5" gutterBottom component="div">
                                {messages['vidfleet_dms_statuses']}
                            </Typography>
                        </Grid>
                    }
                    {
                        !props?.single &&
                        <div style={{ width: '100%', marginBottom: 10, marginTop: 5 }}>
                            <Divider style={{ backgroundColor: '#999999' }} />
                        </div>
                    }
                    <DataViewInfiniteScroll
                        ref={tableRef}
                        name="pm-dms-statuses-v1"
                        showTotalCount={false}
                        exportConfig={{
                            fileName: "dmsstatuses",
                            disablePDF: true,
                            useDataGrid: true,
                        }}
                        externalDataConfig={{
                            data: Items,
                            loading,
                            nextToken: type === "last" ? false : hasMore,
                            onRefresh: () => getData(true),
                            totalCount: Items?.length || 0,
                            totalCountLoading: loading,
                        }}
                        columns={columns}
                        uiConfig={{
                            topHeight: props?.single ? 286 : 200,
                            hideSplit: true,
                            hideFilters: true,
                        }}
                        searchConfig={{
                            disabled: true,
                        }}
                        selectionConfig={{
                            disabled: true,
                        }}
                        infiniteScroll={type !== 'last'}
                        onChangePage={() => getData()}
                        onChangeColumns={(items) => setColumns(items)}
                        tableButtons={["export", 'orderColumn']}
                        extraFilters={() => {
                            return (
                                <Filters
                                    startDate={startDate}
                                    endDate={endDate}
                                    setStartDate={setStartDate}
                                    setEndDate={setEndDate}
                                    setBeforeRangeDate={setBeforeRangeDate}
                                    beforeRangeDate={beforeRangeDate}
                                    last={last}
                                    setLast={setLast}
                                    onChangeFilters={() => getData(true)}
                                    type={type}
                                    setType={handleLast}
                                    esn={esn}
                                    setEsn={setEsn}
                                    disabled={loading}
                                    single={props?.single}
                                    module={props?.module}
                                    setModule={props?.setModule}
                                />
                            )
                        }}
                        flexEnd
                        onChangeConditions={handleChangeConditions}
                    />
                </Grid>
            </Paper>
        </Container>
    )
}

export default DMSStatuses;
